import React from 'react';
import { Search, X, Play, Footprints, FileText } from 'lucide-react';

export const matchItems = (items, query) => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return items;
  return items
    .filter((it) => terms.every((t) => it.haystack.includes(t)))
    .sort((a, b) => Number(terms.some((t) => b.title.toLowerCase().includes(t))) - Number(terms.some((t) => a.title.toLowerCase().includes(t))));
};

export const VipSearchBar = ({ value, onChange, placeholder = 'Search all videos & movements — try “hip”, “spine” or “stress”', testId = 'vip-search', className = 'mb-6' }) => (
  <div className={`relative ${className}`} data-testid={testId}>
    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
    <input
      type="text"
      inputMode="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-stone-200 rounded-full pl-11 pr-11 py-3 font-body text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-forest/50 focus:ring-2 focus:ring-forest/10 transition-colors"
      data-testid={`${testId}-input`}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-stone-400 hover:text-forest hover:bg-stone-100"
        aria-label="Clear search"
        data-testid={`${testId}-clear`}
      >
        <X size={16} />
      </button>
    )}
  </div>
);

export const SearchChips = ({ words, active, onPick }) => (
  <div className="flex flex-wrap items-center gap-2 -mt-3 mb-6" data-testid="vip-search-chips">
    <span className="font-body text-xs text-stone-500 mr-1">Try:</span>
    {words.map((w) => {
      const on = active.trim().toLowerCase() === w.toLowerCase();
      return (
        <button
          key={w}
          type="button"
          onClick={() => onPick(on ? '' : w)}
          className={`px-3.5 py-1.5 rounded-full font-body text-xs border transition-colors ${on ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40 hover:text-forest'}`}
          data-testid={`vip-search-chip-${w.toLowerCase()}`}
        >
          {w}
        </button>
      );
    })}
  </div>
);

const Thumb = ({ it }) => {
  if (it.thumb) return <img src={it.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />;
  const Icon = it.kind === 'movement' ? Footprints : FileText;
  return <Icon size={20} className="text-[#8a6a5a]" />;
};

export const VipSearchResults = ({ query, results, onPick }) => (
  <section data-testid="vip-search-results">
    <p className="font-body text-sm text-stone-500 mb-4" data-testid="vip-search-count">
      {results.length
        ? `${results.length} result${results.length === 1 ? '' : 's'} for “${query.trim()}”`
        : `Nothing found for “${query.trim()}” yet — try a different word.`}
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {results.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onPick(it)}
          className="group flex items-center gap-4 text-left bg-white border border-stone-200 rounded-xl p-3 hover:border-[#8a6a5a]/50 hover:shadow-md transition-shadow"
          data-testid={`vip-search-result-${it.key}`}
        >
          <div className="relative w-24 aspect-video rounded-lg overflow-hidden bg-[#f3ece0] flex items-center justify-center flex-shrink-0">
            <Thumb it={it} />
            {it.kind === 'video' && it.thumb && (
              <span className="absolute inset-0 flex items-center justify-center bg-forest/20">
                <Play size={16} className="text-cream" />
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-body uppercase tracking-[0.15em] text-[#8a6a5a] mb-0.5 leading-snug">{it.where}</p>
            <p className="font-heading text-base text-forest leading-snug group-hover:text-forest-dark">{it.title}</p>
          </div>
        </button>
      ))}
    </div>
  </section>
);
